import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Grid,
  Group,
  LoadingOverlay,
  Modal,
  Pagination,
  Paper,
  Select,
  Text,
  TextInput,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { useDebouncedValue } from "@mantine/hooks";
import dayjs from "dayjs";
import "jspdf-autotable";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  FaCalendar,
  FaDownload,
  FaEdit,
  FaFilePdf,
  FaKeyboard,
  FaPlus,
  FaSync,
  FaTag,
  FaTrash,
} from "react-icons/fa";
import { MdSend } from "react-icons/md";
import { Label } from "reactstrap";
import { v5 as uuidv5 } from "uuid";
import { ApiGetAllPeriodos } from "../../../api/periodos";
import { ApiGetAllComidas } from "../../../api/comidas";
import {
  ApiCrearTicket,
  ApiDesactivarTicket,
  ApiGetAllTicketsWithDate,
  ApiGetTotalTicketWithDates,
  ApiUpdateTicket,
} from "../../../api/tickets";
import { ApiGetUserByCode } from "../../../api/usuarios";
import { socket } from "../../../config/socket";
import { buildRandomDateInMealHours, dateLocal } from "../../../utils/date";
import {
  exportExcel,
  exportExcelRecuento,
  exportTicketsPDF,
} from "../../../utils/exportFiles";
import BulkTicketUpload from "../../../components/admin/tickets/CargaMasiva";
import CreateTicketTeclado from "../../../components/admin/tickets/CreateTicketTeclado";
import Form from "../../../components/admin/tickets/Form";
import ModalRecuentosPorComidas from "../../../components/admin/tickets/ModalRecuentosXComidas";
const UUID_NAMESPACE = "1b671a64-40d5-491e-99b0-da01ff1f3341";

const TicketsAdmin = () => {
  const [state, setState] = useState({
    tickets: [],
    comidas: [],
    comidasDisponibles: [],
    usuarios: [],
    periodos: [],
    loading: true,
    showForm: false,
    selectedTicket: null,
  });

  const [filters, setFilters] = useState({
    periodo: "",
    comida: "",
    searchQuery: "",
    code: "",
    startDate: dayjs().subtract(7, "day").toDate(),
    endDate: new Date(),
  });
  const [appliedDates, setAppliedDates] = useState({
    startDate: dayjs().subtract(7, "day").toDate(),
    endDate: new Date(),
  });

  const [formData, setFormData] = useState({
    pre_usuario_id: "",
    pre_comida_id: "",
    pre_periodo_id: "",
    create_at: dayjs().format("DD-MM-YYYY HH:mm"),
    uuid4: "",
    user_code: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [totalTickets, setTotalTickets] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [ticketsForRecuento, setTicketsForRecuento] = useState([]);
  const ticketsFilterKeyRef = useRef("");
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [debouncedSearch] = useDebouncedValue(filters.searchQuery, 1000);
  const [debouncedCode] = useDebouncedValue(filters.code, 1000);

  //crear ticket con teclado numérico
  const [showNumericModal, setShowNumericModal] = useState(false);
  const [numericCode, setNumericCode] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selectedMeal, setSelectedMeal] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    dayjs(new Date()).tz("America/Guayaquil").format("YYYY-MM-DD HH:mm:ss"),
  );

  const [submitting, setSubmitting] = useState(false);
  const [resumenData, setResumenData] = useState([]);
  const [showResumen, setShowResumen] = useState(false);

  const [selectedTime, setSelectedTime] = useState("");

  const mealNamesFromPeriodos = Array.from(
    new Set(
      (state.periodos || []).flatMap((periodo) =>
        (periodo.comidas || [])
          .map((comida) => (comida?.label || "").toString().trim())
          .filter(Boolean),
      ),
    ),
  );

  const availableMealNames = Array.from(
    new Set([
      ...(state.comidasDisponibles || []),
      ...mealNamesFromPeriodos,
    ]),
  ).filter((name) => typeof name === "string" && name.length > 0);

  const buildTicketQueryFilters = useCallback(
    () => ({
      search: debouncedSearch || "",
      code: debouncedCode || "",
      comida: filters.comida || "",
      periodo: filters.periodo || "",
    }),
    [debouncedSearch, debouncedCode, filters.comida, filters.periodo],
  );

  const fetchTicketsPage = useCallback(
    async (page = 1, pageSize = itemsPerPage, dates = appliedDates) => {
      try {
        const inicio = dayjs(dates.startDate).format("DD-MM-YYYY");
        const fin = dayjs(dates.endDate).format("DD-MM-YYYY");
        const queryFilters = buildTicketQueryFilters();

        const res = await ApiGetAllTicketsWithDate(
          inicio,
          fin,
          page,
          pageSize,
          undefined,
          queryFilters,
        );

        if (res.sms === "ok") {
          return {
            tickets: res.data?.tickets || [],
            total: Number(res.data?.total) || 0,
            totalPages: Number(res.data?.totalPaginas) || 0,
            page: Number(res.data?.paginaActual) || page,
          };
        }
        toast.error(res.mensaje || "Error cargando tickets");
        return { tickets: [], total: 0, totalPages: 0, page: 1 };
      } catch (error) {
        toast.error("Error cargando tickets");
        return { tickets: [], total: 0, totalPages: 0, page: 1 };
      }
    },
    [appliedDates, buildTicketQueryFilters],
  );

  const fetchAllTickets = useCallback(async () => {
    try {
      const inicio = dayjs(appliedDates.startDate).format("DD-MM-YYYY");
      const fin = dayjs(appliedDates.endDate).format("DD-MM-YYYY");
      const queryFilters = buildTicketQueryFilters();
      const exportPageSize = 600;

      const resTotal = await ApiGetTotalTicketWithDates(
        inicio,
        fin,
        queryFilters,
      );
      const total = Number(resTotal?.data) || 0;
      if (total <= 0) return [];

      const totalPaginas = Math.ceil(total / exportPageSize);
      let tickets = [];

      for (let x = 1; x <= totalPaginas; x++) {
        const res = await ApiGetAllTicketsWithDate(
          inicio,
          fin,
          x,
          exportPageSize,
          total,
          queryFilters,
        );
        if (res.sms === "ok") {
          tickets = tickets.concat(res.data?.tickets || []);
        }
      }
      return tickets;
    } catch (error) {
      toast.error("Error cargando tickets para exportar");
      return [];
    }
  }, [appliedDates, buildTicketQueryFilters]);

  const fetchPeriodo = useCallback(async () => {
    try {
      const res = await ApiGetAllPeriodos();
      if (res.sms === "ok") {
        return res.data;
      }
      return null;
    } catch (error) {
      toast.error("Error cargando período");
      return null;
    }
  }, []);

  const fetchComidas = useCallback(async () => {
    try {
      const res = await ApiGetAllComidas();
      if (res.sms === "ok") {
        return (res.data || [])
          .filter((c) => c?.estado !== false)
          .map((c) => (c?.nombre || "").toString().trim())
          .filter(Boolean);
      }
      return [];
    } catch (error) {
      toast.error("Error cargando comidas");
      return [];
    }
  }, []);

  const refreshTicketsWithFilters = useCallback(
    async (page = currentPage, dates = appliedDates) => {
      setState((prev) => ({ ...prev, loading: true }));
      try {
        const result = await fetchTicketsPage(page, itemsPerPage, dates);
        setState((prev) => ({
          ...prev,
          tickets: result.tickets,
          loading: false,
        }));
        setTotalTickets(result.total);
        setTotalPages(
          result.totalPages || Math.ceil(result.total / itemsPerPage) || 0,
        );
      } catch (error) {
        toast.error("Error al actualizar listado de tickets");
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [fetchTicketsPage, currentPage, appliedDates],
  );

  const fetchResources = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));

    const [periodos, comidasDisponibles] = await Promise.all([
      fetchPeriodo(),
      fetchComidas(),
    ]);

    setState((prev) => ({
      ...prev,
      comidasDisponibles,
      usuarios: [],
      periodos: (periodos || []).map((u) => ({
        value: u.pre_periodo_id,
        label: `${u.nombre} (${u.fecha_inicio} - ${u.fecha_fin})`,
        minDate: dayjs(u.fecha_inicio).format("YYYY-MM-DDTHH:mm"),
        maxDate: dayjs(u.fecha_fin).format("YYYY-MM-DDTHH:mm"),
        comidas: (u.pre_comidas_periodo || []).map((c) => ({
          value: c.pre_comidas.pre_comida_id,
          label: c.pre_comidas.nombre,
          periodo: u.pre_periodo_id,
          minTime: c.pre_comidas.hora_inicio,
          maxTime: c.pre_comidas.hora_fin,
        })),
      })),
      loading: false,
    }));
  }, [fetchPeriodo, fetchComidas]);

  useEffect(() => {
    fetchResources();

    socket.emit("join:ticket", "ticketsEmitidos");
    socket.on("ticket:updated", handleSocketUpdate);

    return () => {
      socket.off("ticket:updated", handleSocketUpdate);
      socket.disconnect();
    };
  }, [fetchResources]);

  const handleSocketUpdate = (data) => {
    setState((prev) => {
      const tickets = [...prev.tickets];
      const index = tickets.findIndex(
        (t) => t.pre_ticket_id === data.pre_ticket_id,
      );

      if (data.action === "delete") {
        return {
          ...prev,
          tickets: tickets.filter((t) => t.pre_ticket_id !== data.id),
        };
      }

      if (index === -1) {
        return { ...prev, tickets: [data, ...tickets] };
      }

      tickets[index] = data;
      return { ...prev, tickets };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isUpdate = !!state.selectedTicket;
    if (formData.pre_periodo_id === "") {
      toast.error("Seleccione un periodo");
      return;
    }
    if (formData.pre_comida_id === "") {
      toast.error("Seleccione una comida");
      return;
    }
    if (formData.pre_usuario_id === "") {
      toast.error("Seleccione un usuario");
      return;
    }
    if (formData.create_at === "") {
      toast.error("Seleccione una fecha");
      return;
    }

    try {
      const userCode =
        formData.user_code ||
        state.selectedTicket?.pre_usuarios?.code ||
        "";

      const isSpecialCode = [
        "V001",
        "E001",
        "G001",
        "P001",
        "X001",
        "S001",
        "T001",
      ].includes(userCode);
      if (!isUpdate) {
        formData.uuid4 = uuidv5(
          `${formData.pre_usuario_id}-${formData.pre_comida_id}-${dayjs(
            formData.create_at,
          ).format("YYYYMMDD")}${isSpecialCode ? `-${Date.now()}` : ""}`,
          UUID_NAMESPACE,
        );
      }
      setState((prev) => ({ ...prev, loading: true }));

      const period = state.periodos.find(
        (p) => String(p.value) === String(formData.pre_periodo_id),
      );
      const meal = period?.comidas?.find(
        (c) => String(c.value) === String(formData.pre_comida_id),
      );
      if (!meal?.minTime || !meal?.maxTime) {
        toast.error(
          "La comida seleccionada no tiene horario (hora inicio/fin) definido",
        );
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }

      const fechaBase = formData.create_at || selectedDate;
      const fecha = buildRandomDateInMealHours(
        fechaBase,
        meal.minTime,
        meal.maxTime,
      );

      formData.create_at = dateLocal(fecha);
      formData.manual = true;

      const response = isUpdate
        ? await ApiUpdateTicket(state.selectedTicket.pre_ticket_id, formData)
        : await ApiCrearTicket(formData);

      if (response.sms !== "ok" || response.code === "limitcomidauser") {
        toast.error(response.mensaje || "Error en la operación");
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }

      socket.emit("ticket:update", {
        sala: "ticketsEmitidos",
        data: { ...response.data, action: isUpdate ? "update" : "create" },
      });

      toast.success(
        response.mensaje || `Ticket ${isUpdate ? "actualizado" : "creado"}`,
      );
      resetForm();
      await refreshTicketsWithFilters(currentPage);
    } catch (error) {
      toast.error(error.mensaje);
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleNumericSubmit = async () => {
    if (!selectedPeriod || !selectedMeal || !numericCode) {
      toast.error("Complete todos los campos requeridos");
      return;
    }

    setSubmitting(true);

    const userRes = await ApiGetUserByCode(numericCode.trim());
    if (userRes.sms !== "ok" || !userRes.data) {
      toast.error(userRes.mensaje || "Usuario no encontrado");
      setSubmitting(false);
      return;
    }

    const user = {
      value: userRes.data.pre_usuario_id,
      code: userRes.data.code,
      usuario: userRes.data.usuario,
    };

    const period = state.periodos.find((p) => p.value === selectedPeriod);
    const meal = period?.comidas?.find(
      (c) => String(c.value) === String(selectedMeal),
    );
    const minTime = meal?.minTime || null;
    const maxTime = meal?.maxTime || null;

    if (minTime && maxTime) {
      let isValidTime;

      if (minTime <= maxTime) {
        // Rango normal: 09:00 - 14:00
        isValidTime = selectedTime >= minTime && selectedTime <= maxTime;
      } else {
        // Rango que cruza medianoche: 23:00 - 02:00
        isValidTime = selectedTime >= minTime || selectedTime <= maxTime;
      }

      if (!isValidTime) {
        toast.error(`La hora debe estar entre ${minTime} y ${maxTime}`);
        setSubmitting(false);
        return;
      }
    }

    const isSpecialCode = [
      "V001",
      "E001",
      "G001",
      "P001",
      "X001",
      "S001",
      "T001",
    ].includes(user.code || numericCode);

    const tipoComida = parseInt(selectedMeal);
    const randInt = (min, max) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    let fecha;

    // if (tipoComida === 1) {
    //   // entre 09:00:00 y 14:59:59
    //   const hour = randInt(9, 14);
    //   const minute = randInt(0, 59);
    //   const second = randInt(0, 59);

    //   fecha = dayjs(selectedDate)
    //     .hour(hour)
    //     .minute(minute)
    //     .second(second)
    //     .format("YYYY-MM-DD HH:mm:ss");
    // } else {
    //   // entre 18:00:00 y 23:59:59
    //   const hour = randInt(18, 23);
    //   const minute = randInt(0, 59);
    //   const second = randInt(0, 59);

    //   fecha = dayjs(selectedDate)
    //     .hour(hour)
    //     .minute(minute)
    //     .second(second)
    //     .format("YYYY-MM-DD HH:mm:ss");
    // }

    const [h, m] = selectedTime.split(":").map((n) => parseInt(n, 10));

    let fechaDayjs = dayjs(selectedDate)
      .hour(h)
      .minute(m)
      .second(0)
      .millisecond(0);

    // Si la comida cruza medianoche y la hora seleccionada
    // pertenece al día siguiente, avanzamos un día.
    //
    // Ejemplo:
    // Rango: 23:00 - 02:00
    // Fecha seleccionada: 21/07/2026
    // Hora: 01:00
    //
    // Resultado:
    // 22/07/2026 01:00:00

    if (minTime && maxTime && minTime > maxTime && selectedTime <= maxTime) {
      fechaDayjs = fechaDayjs.add(1, "day");
    }

    fecha = fechaDayjs.format("YYYY-MM-DD HH:mm:ss");

    try {
      const newTicket = {
        pre_usuario_id: user.value,
        pre_comida_id: selectedMeal,
        pre_periodo_id: selectedPeriod,
        create_at: dateLocal(fecha),
        uuid4: uuidv5(
          `${user.value}-${selectedMeal}-${dayjs(selectedDate).format(
            "YYYYMMDD",
          )}${isSpecialCode ? `-${Date.now()}` : ""}`,
          UUID_NAMESPACE,
        ),
        manual: true,
      };

      const hasDuplicate = state.tickets.some(
        (t) =>
          t.pre_usuario_id === newTicket.pre_usuario_id &&
          t.pre_comida_id === newTicket.pre_comida_id &&
          dayjs(t.create_at).isSame(selectedDate, "day"),
      );

      if (hasDuplicate && !isSpecialCode) {
        toast.error(
          "Ya existe un ticket para este usuario y comida en esta fecha",
        );
        return;
      }

      const response = await ApiCrearTicket(newTicket);

      if (response.sms === "ok") {
        toast.success(response.mensaje || "Ticket creado exitosamente");
        socket.emit("ticket:update", {
          sala: "ticketsEmitidos",
          data: { ...response.data, action: "create" },
        });
        setNumericCode("");
        // await refreshTicketsWithFilters();
      } else {
        throw new Error(response.mensaje || "Error al crear ticket");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Confirmar eliminación?")) return;

    try {
      setState((prev) => ({ ...prev, loading: true }));
      const response = await ApiDesactivarTicket(id);
      if (response.sms !== "ok") {
        toast.error(response.mensaje || "Error al eliminar el ticket");
        return;
      }
      toast.success("Ticket eliminado");
      setSelectedTicketIds((prev) => prev.filter((x) => x !== id));
      await refreshTicketsWithFilters(currentPage);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const toggleTicketSelection = (ticketId) => {
    setSelectedTicketIds((prev) =>
      prev.includes(ticketId)
        ? prev.filter((id) => id !== ticketId)
        : [...prev, ticketId],
    );
  };

  const pageTicketIds = state.tickets.map((t) => t.pre_ticket_id);
  const allPageSelected =
    pageTicketIds.length > 0 &&
    pageTicketIds.every((id) => selectedTicketIds.includes(id));
  const somePageSelected =
    pageTicketIds.some((id) => selectedTicketIds.includes(id)) &&
    !allPageSelected;

  const toggleSelectAllPage = () => {
    if (allPageSelected) {
      setSelectedTicketIds((prev) =>
        prev.filter((id) => !pageTicketIds.includes(id)),
      );
      return;
    }
    setSelectedTicketIds((prev) => [
      ...new Set([...prev, ...pageTicketIds]),
    ]);
  };

  const selectedTickets = state.tickets.filter((t) =>
    selectedTicketIds.includes(t.pre_ticket_id),
  );

  const openBulkDeleteModal = () => {
    if (!selectedTicketIds.length) {
      toast.error("Seleccione al menos un ticket");
      return;
    }
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    if (!selectedTicketIds.length) return;

    setBulkDeleting(true);
    try {
      const ids = [...selectedTicketIds];
      const results = await Promise.allSettled(
        ids.map((id) => ApiDesactivarTicket(id)),
      );

      let ok = 0;
      let fail = 0;
      results.forEach((r) => {
        if (r.status === "fulfilled" && r.value?.sms === "ok") ok += 1;
        else fail += 1;
      });

      if (ok > 0 && fail === 0) {
        toast.success(`Se eliminaron ${ok} tickets`);
      } else if (ok > 0) {
        toast(`Eliminados: ${ok}. Errores: ${fail}`, { icon: "⚠️" });
      } else {
        toast.error("No se pudo eliminar ningún ticket");
      }

      setSelectedTicketIds([]);
      setShowBulkDeleteModal(false);
      await refreshTicketsWithFilters(currentPage);
    } catch (error) {
      toast.error(error.message || "Error al eliminar tickets");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleEdit = (ticket) => {
    setState((prev) => ({
      ...prev,
      selectedTicket: ticket,
      showForm: true,
      comidas: state.periodos.find((p) => p.value === ticket.pre_periodo_id)
        ?.comidas,
    }));
    setFormData({
      pre_usuario_id: ticket.pre_usuario_id,
      pre_comida_id: ticket.pre_comida_id,
      pre_periodo_id: ticket.pre_periodo_id,
      create_at: dayjs(ticket.create_at)
        .tz("America/Guayaquil")
        .format("YYYY-MM-DD"),
      uuid4: ticket.uuid4,
      user_code: ticket.pre_usuarios?.code || "",
    });
  };

  const resetForm = () => {
    setFormData({
      pre_usuario_id: "",
      pre_comida_id: "",
      pre_periodo_id: "",
      create_at: dayjs().tz("America/Guayaquil").format("YYYY-MM-DD"),
      uuid4: "",
      user_code: "",
    });
    setState((prev) => ({ ...prev, showForm: false, selectedTicket: null }));
  };

  useEffect(() => {
    let cancelled = false;
    const filterKey = JSON.stringify({
      search: debouncedSearch,
      code: debouncedCode,
      comida: filters.comida,
      periodo: filters.periodo,
      start: dayjs(appliedDates.startDate).format("YYYY-MM-DD"),
      end: dayjs(appliedDates.endDate).format("YYYY-MM-DD"),
    });

    const load = async () => {
      setState((prev) => ({ ...prev, loading: true }));
      const result = await fetchTicketsPage(currentPage, itemsPerPage);
      if (cancelled) return;
      setState((prev) => ({
        ...prev,
        tickets: result.tickets,
        loading: false,
      }));
      setTotalTickets(result.total);
      setTotalPages(
        result.totalPages || Math.ceil(result.total / itemsPerPage) || 0,
      );
    };

    // Si cambian filtros y no estamos en página 1, solo resetear página
    // (el siguiente render con page=1 disparará la carga).
    if (
      ticketsFilterKeyRef.current &&
      ticketsFilterKeyRef.current !== filterKey &&
      currentPage !== 1
    ) {
      ticketsFilterKeyRef.current = filterKey;
      setCurrentPage(1);
      return () => {
        cancelled = true;
      };
    }

    ticketsFilterKeyRef.current = filterKey;
    setSelectedTicketIds([]);
    load();
    return () => {
      cancelled = true;
    };
  }, [
    currentPage,
    debouncedSearch,
    debouncedCode,
    filters.comida,
    filters.periodo,
    appliedDates,
    fetchTicketsPage,
  ]);

  const handleDateFilter = () => {
    setAppliedDates({
      startDate: filters.startDate,
      endDate: filters.endDate,
    });
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const allTickets = await fetchAllTickets();
      if (!allTickets.length) {
        toast.error("No hay tickets para exportar");
        return;
      }
      exportExcel(allTickets, filters);
    } finally {
      setExporting(false);
    }
  };

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const allTickets = await fetchAllTickets();
      if (!allTickets.length) {
        toast.error("No hay tickets para exportar");
        return;
      }
      exportTicketsPDF(allTickets, filters, availableMealNames);
    } finally {
      setExporting(false);
    }
  };

  const exportToExcelRecuento = () => {
    exportExcelRecuento(resumenData, filters);
  };

  const buildResumen = (tickets) => {
    const map = new Map();
    tickets.forEach((t) => {
      const code = t?.pre_usuarios?.code;
      if (!code) return;

      if (!map.has(code)) {
        map.set(code, {
          code,
          countAlmuerzo: 0,
          countMerienda: 0,
          sample: t,
        });
      }
      if (t.pre_comidas?.nombre?.toLowerCase() === "almuerzo") {
        map.get(code).countAlmuerzo += 1;
      } else if (t.pre_comidas?.nombre?.toLowerCase() === "merienda") {
        map.get(code).countMerienda += 1;
      }
    });

    return Array.from(map.values()).sort(
      (a, b) => parseInt(a.code) - parseInt(b.code),
    );
  };

  const openRecuento = async () => {
    setExporting(true);
    try {
      const allTickets = await fetchAllTickets();
      setTicketsForRecuento(allTickets);
      setResumenData(buildResumen(allTickets));
      setShowResumen(true);
    } finally {
      setExporting(false);
    }
  };

  // const exportTotalTicketsToPDF = () => {
  //   exportTotalTicketsPDF(filteredTickets);
  // };

  return (
    <Box p="md">
      <LoadingOverlay visible={state.loading || exporting} overlayBlur={2} />

      <Group position="apart" mb="md">
        <Group style={{ display: "flex", flexDirection: "column" }}>
          <Box style={{ display: "flex", gap: "10px" }}>
            <Button
              leftIcon={<FaPlus size={20} />}
              onClick={() => setState((prev) => ({ ...prev, showForm: true }))}
            >
              Nuevo Ticket Manual
            </Button>
            <Button
              leftIcon={<FaKeyboard size={20} />}
              onClick={() => setShowNumericModal(true)}
              variant="outline"
            >
              Nuevo con Teclado
            </Button>
          </Box>
          <Box>
            <BulkTicketUpload
              periodos={state.periodos}
              onSuccess={() => refreshTicketsWithFilters(1)}
            />
          </Box>
        </Group>

        <Group spacing="sm">
          <Group
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              backgroundColor: "#f5f5f5",
            }}
          >
            <DatePicker
              label="Fecha inicial"
              value={filters.startDate}
              onChange={(date) =>
                setFilters((prev) => ({
                  ...prev,
                  startDate: date || dayjs().subtract(7, "day").toDate(),
                }))
              }
              maxDate={new Date()}
              icon={<FaCalendar size={14} />}
            />
            <DatePicker
              label="Fecha final"
              value={filters.endDate}
              onChange={(date) =>
                setFilters((prev) => ({
                  ...prev,
                  endDate: date || new Date(),
                }))
              }
              // maxDate={new Date()}
              icon={<FaCalendar size={14} />}
            />
            <Button
              onClick={handleDateFilter}
              leftIcon={<FaSync size={14} />}
              style={{ marginTop: 24 }}
            >
              Aplicar Filtro
            </Button>
          </Group>
          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              gap: "10px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Select
              data={[
                { value: "", label: "Todas las comidas" },
                ...availableMealNames.map((meal) => ({
                  value: String(meal).toLowerCase(),
                  label: String(meal),
                })),
              ]}
              value={filters.comida}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, comida: value }))
              }
              placeholder="Seleccione una comida"
            />
            <TextInput
              placeholder="Codigo..."
              value={filters.code}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, code: e.target.value }))
              }
              rightSection={
                <FaSync
                  size={14}
                  onClick={() => refreshTicketsWithFilters(currentPage)}
                  style={{ cursor: "pointer" }}
                />
              }
            />
          </Box>
          <TextInput
            placeholder="Buscar..."
            value={filters.searchQuery}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, searchQuery: e.target.value }))
            }
            rightSection={
              <FaSync
                size={14}
                onClick={() => refreshTicketsWithFilters(currentPage)}
                style={{ cursor: "pointer" }}
              />
            }
          />
        </Group>
      </Group>

      <Group
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Label style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
          Total de Tickets: {totalTickets}
        </Label>
        <Group>
          {/* <Button
            onClick={exportTotalTicketsToPDF}
            leftIcon={<FaFilePdf size={14} />}
            style={{ marginTop: 24 }}
            color="gray"
          >
            Exportar PDF
          </Button> */}
          <Button
            onClick={openRecuento}
            leftIcon={<FaTag size={14} />}
            style={{ marginTop: 24 }}
            color="grape"
            loading={exporting}
          >
            Ver Recuento
          </Button>
          <Button
            onClick={exportToExcel}
            leftIcon={<FaDownload size={14} />}
            style={{ marginTop: 24 }}
            color="green"
            loading={exporting}
          >
            Exportar Excel
          </Button>
          <Button
            onClick={exportToPDF}
            leftIcon={<FaFilePdf size={14} />}
            style={{ marginTop: 24 }}
            color="orange"
            loading={exporting}
          >
            Exportar PDF
          </Button>
        </Group>
      </Group>

      {state.showForm && (
        <Form
          state={state}
          setState={setState}
          formData={formData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          resetForm={resetForm}
        />
      )}
      <Box mb="md">
        <Pagination
          page={currentPage}
          onChange={handlePageChange}
          total={Math.max(totalPages, 1)}
          mt="md"
          position="center"
        />
      </Box>

      {state.tickets.length > 0 && (
        <Group mb="sm" spacing="sm">
          <Checkbox
            checked={allPageSelected}
            indeterminate={somePageSelected}
            onChange={toggleSelectAllPage}
            label={`Seleccionar todos (${state.tickets.length})`}
          />
        </Group>
      )}

      {selectedTicketIds.length > 0 && (
        <Paper
          withBorder
          p="sm"
          mb="md"
          style={{
            position: "sticky",
            top: 8,
            zIndex: 20,
            background: "#fff8f8",
            borderColor: "#f5c2c7",
          }}
        >
          <Group position="apart">
            <Text size="sm">
              <strong>{selectedTicketIds.length}</strong> ticket
              {selectedTicketIds.length === 1 ? "" : "s"} seleccionado
              {selectedTicketIds.length === 1 ? "" : "s"}
            </Text>
            <Group spacing="xs">
              <Button
                variant="default"
                size="xs"
                onClick={() => setSelectedTicketIds([])}
              >
                Limpiar selección
              </Button>
              <Button
                color="red"
                size="xs"
                leftIcon={<FaTrash size={12} />}
                onClick={openBulkDeleteModal}
              >
                Eliminar seleccionados
              </Button>
            </Group>
          </Group>
        </Paper>
      )}

      <Grid>
        {state.tickets.map((ticket) => {
          const isSelected = selectedTicketIds.includes(ticket.pre_ticket_id);
          return (
            <Grid.Col key={ticket.pre_ticket_id} span={4}>
              <Paper
                withBorder
                p="md"
                mb="sm"
                style={{
                  borderColor: isSelected ? "#fa5252" : undefined,
                  background: isSelected ? "#fff5f5" : undefined,
                  cursor: "pointer",
                }}
                onClick={() => toggleTicketSelection(ticket.pre_ticket_id)}
              >
                <Group position="apart" align="flex-start" noWrap>
                  <Group align="flex-start" spacing="sm" noWrap style={{ flex: 1 }}>
                    <Checkbox
                      checked={isSelected}
                      onChange={() =>
                        toggleTicketSelection(ticket.pre_ticket_id)
                      }
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Seleccionar ticket ${ticket.pre_ticket_id}`}
                      mt={4}
                    />
                    <div>
                      <Text weight={600}>
                        {ticket.pre_usuarios?.nombres +
                          " " +
                          ticket.pre_usuarios?.apellidos ||
                          "Usuario desconocido"}
                        {" - "}
                        {ticket.pre_usuarios?.code || "Código no especificado"}
                      </Text>
                      <Text size="sm" color="dimmed">
                        {ticket.pre_comidas?.nombre || "Comida no especificada"}
                      </Text>
                      <Text size="sm">
                        {dayjs(ticket.create_at)
                          .tz("America/Guayaquil")
                          .format("DD/MM/YYYY HH:mm")}
                      </Text>
                      <Badge color={ticket.sendmail ? "green" : "red"}>
                        {ticket.sendmail ? <MdSend /> : "Sin enviar"}
                      </Badge>
                    </div>
                  </Group>

                  <Group spacing="xs" onClick={(e) => e.stopPropagation()}>
                    <ActionIcon color="blue" onClick={() => handleEdit(ticket)}>
                      <FaEdit size={18} />
                    </ActionIcon>
                    <ActionIcon
                      color="red"
                      onClick={() => handleDelete(ticket.pre_ticket_id)}
                    >
                      <FaTrash size={18} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Paper>
            </Grid.Col>
          );
        })}
      </Grid>

      <Modal
        opened={showBulkDeleteModal}
        onClose={() => !bulkDeleting && setShowBulkDeleteModal(false)}
        title="Confirmar eliminación"
        size="lg"
        closeOnClickOutside={!bulkDeleting}
        closeOnEscape={!bulkDeleting}
      >
        <Text mb="sm">
          Vas a eliminar{" "}
          <strong>{selectedTickets.length}</strong> ticket
          {selectedTickets.length === 1 ? "" : "s"}. Esta acción no se puede
          deshacer fácilmente.
        </Text>

        <Box
          mb="md"
          style={{
            maxHeight: 280,
            overflowY: "auto",
            border: "1px solid #dee2e6",
            borderRadius: 8,
            padding: 8,
          }}
        >
          {selectedTickets.map((ticket) => (
            <Paper key={ticket.pre_ticket_id} withBorder p="xs" mb="xs">
              <Text size="sm" weight={600}>
                {ticket.pre_usuarios?.nombres} {ticket.pre_usuarios?.apellidos}{" "}
                ({ticket.pre_usuarios?.code || "sin código"})
              </Text>
              <Text size="xs" color="dimmed">
                {ticket.pre_comidas?.nombre || "Comida"} ·{" "}
                {dayjs(ticket.create_at)
                  .tz("America/Guayaquil")
                  .format("DD/MM/YYYY HH:mm")}
              </Text>
            </Paper>
          ))}
        </Box>

        <Group position="right">
          <Button
            variant="default"
            onClick={() => setShowBulkDeleteModal(false)}
            disabled={bulkDeleting}
          >
            Cancelar
          </Button>
          <Button
            color="red"
            leftIcon={<FaTrash size={12} />}
            loading={bulkDeleting}
            onClick={confirmBulkDelete}
          >
            Eliminar {selectedTickets.length}
          </Button>
        </Group>
      </Modal>

      <CreateTicketTeclado
        showNumericModal={showNumericModal}
        setShowNumericModal={setShowNumericModal}
        state={state}
        numericCode={numericCode}
        setNumericCode={setNumericCode}
        selectedPeriod={selectedPeriod}
        setSelectedPeriod={setSelectedPeriod}
        selectedMeal={selectedMeal}
        setSelectedMeal={setSelectedMeal}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        selectedTime={selectedTime}
        setSelectedTime={setSelectedTime}
        handleNumericSubmit={handleNumericSubmit}
        submitting={submitting}
      />
      <ModalRecuentosPorComidas
        showResumen={showResumen}
        setShowResumen={setShowResumen}
        comidasFiltered={ticketsForRecuento}
        buildResumen={buildResumen}
        exportToExcelRecuento={exportToExcelRecuento}
        setResumenData={setResumenData}
        resumenData={resumenData}
      />
    </Box>
  );
};

export default TicketsAdmin;
